import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "antd";
import { getProjectWiseCost } from "../../redux/adminUnitReportSlice/adminUnitReportSlice";
import { getAllUnits } from "../../redux/unitSlice/unitSlice";
import { fetchProducts } from "../../redux/productSlice/productSlice";
import { fetchPackages } from "../../redux/packageSlice/packageSlice";
import CustomTable from "../../components/Common/CustomTable/CustomTable";
import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";
import PMGProjectIcon from "../../assets/icons/pmg-project.svg";
import { Loader2 } from "lucide-react";

const COST_LEVEL_OPTIONS = [
    { label: "Direct Material", value: "DIRECT_MATERIAL" },
    { label: "Conversion Cost", value: "CONVERSION_COST" },
    { label: "Factory Cost Level", value: "TOTAL_COST" },
];

const VALUE_MODE_OPTIONS = [
    { label: "Crore", value: "CRORE" },
    { label: "Percent", value: "PERCENT" },
];

const MONTH_ABBREVIATIONS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const QUARTER_KEYS = ["Q1", "Q2", "Q3", "Q4"];

// Values from the API can come formatted with thousands separators
// (e.g. "10,518.00"). parseFloat stops at the first comma, so strip
// commas before parsing.
const toNumber = (val) => {
    const cleaned = String(val ?? "").replace(/,/g, "").trim();
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
};

// Builds a normalized "IO_NO-REV_NO" key so matching works regardless of
// whether the two APIs send numbers, strings, extra whitespace, or missing
// REV_NO.
const makeProjectKey = (ioNo, revNo) => {
    const io = String(ioNo ?? "").trim();
    const rev = String(revNo ?? "0").trim();
    return `${io}-${rev}`;
};

// Appends a "%" suffix to a displayed cell value when Value Mode is Percent.
const formatCellValue = (val, valueMode) => {
    if (val === undefined || val === null || val === "") return val;
    return valueMode === "PERCENT" ? `${val}%` : val;
};

// The financial year (Apr -> Mar) that "today" falls in, expressed as its
// starting calendar year, e.g. April 2026 - March 2027 -> 2026.
// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
    const today = new Date();
    return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
    if (!startDate) return null;

    const [day, month, year] = startDate.split("-").map(Number);

    if (!day || !month || !year) return null;

    return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
// OLD: const DEFAULT_FY_KEY = `${CURRENT_FY_START}-${CURRENT_FY_START + 1}`;
// NEW: both are now computed inside the component from the memoized value (see below).
// const CURRENT_FY_START = getCurrentFYStart();
// const CURRENT_FY_START = "2027";

const getFYStartYear = (fyKey) => Number(String(fyKey).split("-")[0]);

// Business requirement: the FY filter dropdown must always start from FY
// 2026-2027 onward — a fixed floor, independent of CURRENT_FY_START (which
// tracks real-world "today" and is used only for the Overall view's Upto
// figure / shrinking column list, not for this dropdown's lower bound).
const FILTER_BASE_FY_START = 2026;

// "2026-2027" -> "2026-27", used for the FY dropdown / table banner.
const formatFYShort = (fyKey) => {
    const [start, end] = String(fyKey).split("-");
    return `${start}-${String(end).slice(-2)}`;
};

// Quarters are keyed by the FY-START year for every quarter in that FY
// (e.g. "Q4-2026" is Jan-Mar 2027). Months, unlike quarters, are keyed by
// their REAL calendar year — Apr-Dec fall in the FY's start calendar year,
// but Jan/Feb/Mar fall in the following calendar year (e.g. FY 2026-2027 ->
// "Apr-2026".."Dec-2026" then "Jan-2027".."Mar-2027"). Confirmed against a
// live getProjectWiseCost response.
const getMonthKey = (monthAbbrev, fyStartYear) => {
    const calendarYear = ["Jan", "Feb", "Mar"].includes(monthAbbrev) ? fyStartYear + 1 : fyStartYear;
    return `${monthAbbrev}-${calendarYear}`;
};
const getQuarterKey = (quarter, fyStartYear) => `${quarter}-${fyStartYear}`;

// "Upto March 26" style figure for the Overall view: the API's baseline
// "upto" figure plus every financial year that falls strictly before the
// current FY. As real time crosses into a new FY, one more year column folds
// into this figure and stops being rendered separately (see visibleYearLabels
// below) — matches DMReport's identical "Upto Mar" logic for this same API
// shape. fyStart is now passed in explicitly by the component, derived from systemDate.
const getUptoMarchValue = (record, fyStart) => {
    if (!record) return "0.00";
    let sum = toNumber(record.financial_years?.upto);
    Object.entries(record.financial_years || {}).forEach(([fyKey, val]) => {
        if (fyKey === "upto") return;
        if (getFYStartYear(fyKey) < fyStart) {
            sum += toNumber(val);
        }
    });
    return sum.toFixed(2);
};

// OLD: const UPTO_MARCH_LABEL = `Cost Upto Mar '${String(CURRENT_FY_START).slice(-2)}`; (fixed once at import time)
// NEW: computed inside the component from the memoized CURRENT_FY_START (see below).

const DMCriticalProductsReport = () => {
    const dispatch = useDispatch();

    const [costLevel, setCostLevel] = useState("DIRECT_MATERIAL");
    const [valueMode, setValueMode] = useState("PERCENT");
    // const [selectedFY, setSelectedFY] = useState(DEFAULT_FY_KEY);
    const [selectedFY, setSelectedFY] = useState("OVERALL");

    // Master, unfiltered list of projects — used only to populate the
    // "Projects Filter" dropdown.
    const [masterProjects, setMasterProjects] = useState([]);

    // Holds "IO_NO-REV_NO" keys selected in the multi-select. The actual
    // {IO_NO, REV_NO} objects sent to the API are derived via
    // projectKeyToProject below.
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);

    const [unit, setUnit] = useState("ALL");

    // Frontend-only: whether the "Upto March" column is shown. Only relevant
    // (and only offered in the UI) in the Overall view with Value Mode set
    // to Crore - Upto March has no meaning as a percentage figure, and the
    // per-FY view never has an Upto March column at all.
    const [showUptoMarch, setShowUptoMarch] = useState(false);

    // Holds PRODUCT_CODE values (multi-select) — the label shown is
    // PRODUCT_NAME, but the API needs codes, so codes are what we store.
    const [selectedProducts, setSelectedProducts] = useState([]);
    // Holds PACKAGE_ID values (multi-select) — see packageOptions below for
    // why the list isn't narrowed by the selected unit/region.
    const [selectedPackages, setSelectedPackages] = useState([]);

    const { projectWiseCost, gettingProjectWiseCost } = useSelector((state) => state.unitReport);
    const { allUnitsList } = useSelector((state) => state.unit);
    const { products: allProducts } = useSelector((state) => state.product);
    const { packages: allPackages } = useSelector((state) => state.package);
    const { systemDate } = useSelector((state) => state.otherProjects);

    // OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
    // NEW: derive it from the admin-configured System Start Date, falling back
    // to today's date until systemDate has loaded.
    const CURRENT_FY_START = useMemo(
        () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
        [systemDate]
    );

    // OLD: const DEFAULT_FY_KEY = `${CURRENT_FY_START}-${CURRENT_FY_START + 1}`; (fixed once at import time)
    const DEFAULT_FY_KEY = `${CURRENT_FY_START}-${CURRENT_FY_START + 1}`;
    const UPTO_MARCH_LABEL = `Upto Mar '${String(CURRENT_FY_START).slice(-2)}`;

    const reportData = projectWiseCost?.data ?? projectWiseCost ?? null;
    const groups = reportData?.groups ?? [];

    const fetchReport = () => {
        const projectsPayload = selectedProjectKeys
            .map((key) => projectKeyToProject.get(key))
            .filter(Boolean)
            .map(({ IO_NO, REV_NO }) => ({ IO_NO, REV_NO }));

        dispatch(
            getProjectWiseCost({
                PROJECT_TYPE: "ALL",
                COST_LEVEL: costLevel,
                VALUE_MODE: valueMode,
                MNF_UNIT: unit === "ALL" ? undefined : unit,
                projects: projectsPayload,
                PRODUCT_CODE: selectedProducts,
                PACKAGE_ID: selectedPackages,
            })
        );
    };

    // Raw unit records (kept alongside the label/value pairs so UNIT_TYPE is
    // available to decide whether to show the Product or Package filter).
    const allUnitsRaw = useMemo(() => {
        return allUnitsList?.data?.data ?? allUnitsList?.data ?? allUnitsList ?? [];
    }, [allUnitsList]);

    const unitOptions = useMemo(() => {
        const options = allUnitsRaw.map((u) => ({
            label: u.UNIT_DISPLAY_NAME,
            value: u.MNF_UNIT,
        }));
        return [{ label: "All", value: "ALL" }, ...options];
    }, [allUnitsRaw]);

    // getAllUnits sends a numeric TYPE, not a string: 1 -> UNIT (show
    // Product filter), 0 -> REGION (show Package filter). "ALL" or an
    // unmatched unit shows neither.
    const selectedUnitType = useMemo(() => {
        if (unit === "ALL") return null;
        const type = allUnitsRaw.find((u) => u.MNF_UNIT === unit)?.TYPE;
        if (type === 1) return "UNIT";
        if (type === 0) return "REGION";
        return null;
    }, [unit, allUnitsRaw]);

    const showProductFilter = selectedUnitType === "UNIT";
    const showPackageFilter = selectedUnitType === "REGION";

    // Only the selected unit's products, as { label: PRODUCT_NAME, value:
    // PRODUCT_CODE } — PRODUCT_CODE is what the API needs, PRODUCT_NAME is
    // just for display.
    const productOptionsForUnit = useMemo(() => {
        if (!showProductFilter) return [];
        return (allProducts || [])
            .filter((p) => p.UNIT_CODE === unit)
            .map((p) => ({ label: p.PRODUCT_NAME, value: p.PRODUCT_CODE }));
    }, [allProducts, unit, showProductFilter]);

    // Packages carry no unit/region field at all, so unlike Products this
    // list can't be narrowed by the selected Region — every package is
    // shown regardless of which Region is selected. Label is "PACKAGE_NAME"
    // with "PACKAGE_TYPE" in italic underneath; searchLabel keeps typeahead
    // search working since CustomMultiSelect's filter needs a plain string,
    // not the rich JSX label.
    const packageOptions = useMemo(() => {
        if (!showPackageFilter) return [];
        return (allPackages || []).map((p) => ({
            value: p.PACKAGE_ID,
            label: (
                <div>
                    <div>{p.PACKAGE_NAME} <span style={{ fontStyle: "italic", fontSize: 12, color: "#667085" }}>
                        {p.PACKAGE_TYPE}
                    </span></div>

                </div>
            ),
            searchLabel: `${p.PACKAGE_NAME} ${p.PACKAGE_TYPE}`,
        }));
    }, [allPackages, showPackageFilter]);

    // Clear the Product/Package selections whenever the unit changes, so a
    // stale selection can't linger under a hidden filter.
    useEffect(() => {
        setSelectedProducts([]);
        setSelectedPackages([]);
    }, [unit]);

    useEffect(() => {
        dispatch(getAllUnits());
        // "All" pages in one go for filter dropdowns — there's real
        // pagination on these endpoints, but we want the full lists
        // client-side so switching Unit doesn't need a fresh fetch.
        dispatch(fetchProducts({ page: 1, pageSize: 10000 }));
        dispatch(fetchPackages({ page: 1, pageSize: 10000 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Single fetch effect — there's no Project Type filter here (unlike
    // DMReport) to fire a separate mount-time fetch, so no dedup guard is
    // needed: this naturally runs once on mount and again on real filter
    // changes only, thanks to the .join(",") stable key.
    // NOTE: selectedFY is intentionally NOT here — the API already returns
    // every year/quarter/month in one payload, so switching FY is purely a
    // frontend re-slice of existing data.
    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        costLevel,
        valueMode,
        selectedProjectKeys.join(","),
        unit,
        selectedProducts.join(","),
        selectedPackages.join(","),
    ]);

    // Every project across every group, used to populate the "Projects
    // Filter" dropdown. Only capture the full project list when the
    // response was NOT filtered by a project selection (i.e.
    // selectedProjectKeys is empty), so the dropdown's option list stays
    // stable even after the table is filtered down to a subset. Each
    // project's `units` array is kept (and merged across fetches, not
    // overwritten) so the dropdown can be narrowed to "projects present on
    // the selected unit" below, regardless of whether this particular
    // fetch happened to be unit-filtered.
    useEffect(() => {
        if (selectedProjectKeys.length === 0 && groups.length) {
            setMasterProjects((prev) => {
                const map = new Map(
                    prev.map((project) => [makeProjectKey(project.IO_NO, project.REV_NO), project])
                );

                groups.forEach((group) => {
                    group.projects?.forEach((project) => {
                        const key = makeProjectKey(project.IO_NO, project.REV_NO);
                        const mergedUnits = new Map(
                            (map.get(key)?.units || []).map((u) => [u.MNF_UNIT, u])
                        );
                        (project.units || []).forEach((u) => mergedUnits.set(u.MNF_UNIT, u));

                        map.set(key, {
                            IO_NO: project.IO_NO,
                            REV_NO: project.REV_NO,
                            PROJ_NAME: project.PROJ_NAME,
                            units: Array.from(mergedUnits.values()),
                        });
                    });
                });

                return Array.from(map.values());
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]);

    // Narrowed to only projects present on the selected unit — same rule
    // the table body already applies per-row.
    const visibleProjectsForFilter = useMemo(() => {
        if (unit === "ALL") return masterProjects;
        return masterProjects.filter((project) => (project.units || []).some((u) => u.MNF_UNIT === unit));
    }, [masterProjects, unit]);

    // Maps a "IO_NO-REV_NO" key (what the multi-select stores) back to the
    // full { IO_NO, REV_NO, PROJ_NAME } object (what the API needs).
    const projectKeyToProject = useMemo(() => {
        const map = new Map();
        masterProjects.forEach((project) => {
            map.set(makeProjectKey(project.IO_NO, project.REV_NO), project);
        });
        return map;
    }, [masterProjects]);

    // FY 2026-2027 onward, plus "Overall" — fixed floor (FILTER_BASE_FY_START),
    // NOT tied to today's date, so this list stays identical whether the
    // real current FY is 2026-27, 2027-28, or 2028-29 (unlike
    // visibleYearLabels below, which intentionally shrinks over time for the
    // Overall view's columns). Selecting any FY here always shows its
    // month/quarter breakdown.
    const fyOptions = useMemo(() => {
        const labels = reportData?.labels ?? [];
        const yearOptions = labels
            .filter((fy) => getFYStartYear(fy) >= FILTER_BASE_FY_START)
            .sort((a, b) => getFYStartYear(a) - getFYStartYear(b))
            .map((fy) => ({ label: formatFYShort(fy), value: fy }));
        // TEMP: "Overall" hidden for now — uncomment the line below (and
        // remove this one) to bring it back.
        return [{ label: "Overall", value: "OVERALL" }, ...yearOptions];
        // return yearOptions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportData]);

    // Current-FY-onward only — used for the Overall view's rendered
    // columns (kept as full FY keys, "2026-2027", not the short label).
    const visibleYearLabels = useMemo(() => {
        const labels = reportData?.labels ?? [];
        return labels
            .filter((fy) => getFYStartYear(fy) >= CURRENT_FY_START)
            .sort((a, b) => getFYStartYear(a) - getFYStartYear(b));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportData, CURRENT_FY_START]);

    // SWITCH: when true, the Overall view shows only the first
    // BEYOND_SPLIT_YEAR_COUNT financial years (starting at the current FY) as
    // individual columns; every FY beyond that is summed into one
    // "<FY> & Beyond" column instead of being rendered separately.
    // Set to false to restore the old behaviour (every FY in visibleYearLabels
    // gets its own column).
    const SPLIT_OVERALL_YEARS_WITH_BEYOND = false;
    const BEYOND_SPLIT_YEAR_COUNT = 5;

    const { individualYearLabels, beyondYearLabels } = useMemo(() => {
        if (!SPLIT_OVERALL_YEARS_WITH_BEYOND) {
            return { individualYearLabels: visibleYearLabels, beyondYearLabels: [] };
        }
        return {
            individualYearLabels: visibleYearLabels.slice(0, BEYOND_SPLIT_YEAR_COUNT),
            beyondYearLabels: visibleYearLabels.slice(BEYOND_SPLIT_YEAR_COUNT),
        };
    }, [visibleYearLabels]);

    // e.g. "2031-32 & Beyond" — derived from the first year that falls into the
    // combined bucket. Empty when there's nothing left to combine (fewer than
    // BEYOND_SPLIT_YEAR_COUNT + 1 years available from the API).
    const beyondColumnLabel = beyondYearLabels.length
        ? `${formatFYShort(beyondYearLabels[0])} & Beyond`
        : "";

    const isOverallView = selectedFY === "OVERALL";

    const selectedFYStartYear = getFYStartYear(selectedFY);

    const cellRender = (val) => formatCellValue(val, valueMode);

    const columnTotals = useMemo(() => {
        const totals = {};
        individualYearLabels.forEach((year) => {
            totals[`year_${year}`] = 0;
        });

        groups.forEach((group) => {
            (group.projects || [])
                .filter((project) => {
                    if (unit === "ALL") return true;
                    return (project.units || []).some((u) => u.MNF_UNIT === unit);
                })
                .forEach((project) => {
                    if (toNumber(project.total) === 0) return;

                    individualYearLabels.forEach((year) => {
                        const yearVal = project.financial_years?.[year] ?? "0.00";
                        totals[`year_${year}`] += toNumber(yearVal);
                    });
                });
        });

        return totals;
    }, [groups, individualYearLabels, unit]);

    const columns = useMemo(() => {
        const projectColumn = {
            title: "Name of Projects",
            dataIndex: "projectName",
            key: "projectName",
            fixed: "left",
            width: 260,
            render: (text, record) =>
                record.isTotalRow ? <span style={{ fontWeight: 800, fontSize: 16 }}>{text}</span> : text,
        };

        if (isOverallView) {
            // Upto March <yy> + one plain column per FY (>= current FY) +
            // Overall Total — same shape as DMReport's Yearly view.
            const uptoMarchColumn = valueMode === "PERCENT" || !showUptoMarch
                ? []
                : [{
                    title: UPTO_MARCH_LABEL,
                    dataIndex: "uptoMarch",
                    key: "uptoMarch",
                    width: 130,
                    align: "right",
                    render: cellRender,
                }];

            // const yearColumns = visibleYearLabels.map((year) => ({
            //     title: formatFYShort(year),
            //     dataIndex: `year_${year}`,
            //     key: `year_${year}`,
            //     width: 110,
            //     align: "right",
            //     render: cellRender,
            // }));
            const yearColumns = individualYearLabels
                .filter((year) => {
                    const yearKey = `year_${year}`;
                    return (columnTotals[yearKey] ?? 0) !== 0;
                })
                .map((year) => ({
                    title: formatFYShort(year),
                    dataIndex: `year_${year}`,
                    key: `year_${year}`,
                    width: 110,
                    align: "right",
                    render: cellRender,
                }));

            const beyondColumn = beyondYearLabels.length
                ? [{
                    title: beyondColumnLabel,
                    dataIndex: "yearBeyond",
                    key: "yearBeyond",
                    width: 140,
                    align: "right",
                    render: cellRender,
                }]
                : [];

            const overallTotalColumn = {
                title: "Overall Total",
                dataIndex: "total",
                key: "total",
                width: 140,
                align: "right",
                fixed: "right",
                className: "bg-[#EAF2FE]",
                render: (value, record) => {
                    if (valueMode === "PERCENT" || showUptoMarch) {
                        return cellRender(value);
                    }

                    const uptoVal = toNumber(record?.uptoMarch);

                    return cellRender((toNumber(value) - uptoVal).toFixed(2));
                },
            };

            // return [projectColumn, ...uptoMarchColumn, ...yearColumns, overallTotalColumn];
            return [projectColumn, ...uptoMarchColumn, ...yearColumns, ...beyondColumn, overallTotalColumn];
        }

        const fyTotalColumn = {
            title: "FY Total",
            dataIndex: "fyTotal",
            key: "fyTotal",
            width: 120,
            align: "right",
            className: "bg-[#EAF2FE]",
            render: cellRender,
        };

        // Apr, May, Jun, Q1, Jul, Aug, Sep, Q2, Oct, Nov, Dec, Q3, Jan, Feb, Mar, Q4
        const monthQuarterColumns = QUARTER_KEYS.flatMap((quarter, qIdx) => {
            const monthsForQuarter = MONTH_ABBREVIATIONS.slice(qIdx * 3, qIdx * 3 + 3);

            const monthCols = monthsForQuarter.map((abbr) => ({
                title: abbr,
                dataIndex: `m_${abbr}`,
                key: `m_${abbr}`,
                width: 90,
                align: "right",
                render: cellRender,
            }));

            const quarterCol = {
                title: quarter,
                dataIndex: `q_${quarter}`,
                key: `q_${quarter}`,
                width: 100,
                align: "right",
                className: "bg-[#EAF2FE]",
                render: cellRender,
            };

            return [...monthCols, quarterCol];
        });

        // "Name of Projects" stays its own (ungrouped) column so antd spans
        // it across both header rows; everything else nests under a single
        // parent column titled with the selected FY, matching the "Year is
        // the parent header, FY Total..Q4 are its children" layout. Header
        // colors/borders come from the site-wide .custom-ant-table rule
        // (src/index.css) — same as every other table in the app.
        const fyGroupColumn = {
            title: selectedFY,
            key: `fy_group_${selectedFY}`,
            children: [fyTotalColumn, ...monthQuarterColumns],
        };

        return [projectColumn, fyGroupColumn];
    // }, [valueMode, selectedFY, isOverallView, visibleYearLabels]);
    }, [valueMode, selectedFY, isOverallView, individualYearLabels, beyondYearLabels, beyondColumnLabel, CURRENT_FY_START, columnTotals, showUptoMarch]);

    const tableData = useMemo(() => {
        if (isOverallView) {
            const rows = [];

            // const grandRow = { uptoMarch: 0, total: 0 };
            // visibleYearLabels.forEach((year) => {
            //     grandRow[`year_${year}`] = 0;
            // });

            const grandRow = { uptoMarch: 0, total: 0 };
            individualYearLabels.forEach((year) => {
                grandRow[`year_${year}`] = 0;
            });
            if (beyondYearLabels.length) grandRow.yearBeyond = 0;

            groups.forEach((group) => {
                (group.projects || [])
                    .filter((project) => {
                        if (unit === "ALL") return true;
                        return (project.units || []).some((u) => u.MNF_UNIT === unit);
                    })
                    .forEach((project) => {
                        // Skip projects with no overall contribution at all.
                        if (toNumber(project.total) === 0) return;

                        const row = {
                            key: makeProjectKey(project.IO_NO, project.REV_NO),
                            IO_NO: project.IO_NO,
                            projectName: project.PROJ_NAME,
                        };

                        row.uptoMarch = getUptoMarchValue(project, CURRENT_FY_START);
                        grandRow.uptoMarch += toNumber(row.uptoMarch);

                        // visibleYearLabels.forEach((year) => {
                        //     const yearVal = project.financial_years?.[year] ?? "0.00";
                        //     row[`year_${year}`] = yearVal;
                        //     grandRow[`year_${year}`] += toNumber(yearVal);
                        // });

                        // row.total = project.total ?? "0.00";

                        individualYearLabels.forEach((year) => {
                            const yearVal = project.financial_years?.[year] ?? "0.00";
                            row[`year_${year}`] = yearVal;
                            grandRow[`year_${year}`] += toNumber(yearVal);
                        });

                        if (beyondYearLabels.length) {
                            const beyondSum = beyondYearLabels.reduce(
                                (acc, year) => acc + toNumber(project.financial_years?.[year]),
                                0
                            );
                            row.yearBeyond = beyondSum.toFixed(2);
                            grandRow.yearBeyond += beyondSum;
                        }

                        row.total = project.total ?? "0.00";
                        grandRow.total += toNumber(row.total);

                        rows.push(row);
                    });
            });

            if (valueMode === "PERCENT") {
                return rows;
            }

            const totalRow = {
                key: "grand-total",
                projectName: "Total",
                isTotalRow: true,
                uptoMarch: grandRow.uptoMarch.toFixed(2),
                total: grandRow.total.toFixed(2),
            };
            // visibleYearLabels.forEach((year) => {
            //     totalRow[`year_${year}`] = grandRow[`year_${year}`].toFixed(2);
            // });

            individualYearLabels.forEach((year) => {
                totalRow[`year_${year}`] = grandRow[`year_${year}`].toFixed(2);
            });
            if (beyondYearLabels.length) totalRow.yearBeyond = grandRow.yearBeyond.toFixed(2);

            return [...rows, totalRow];
        }

        const rows = [];

        const grandRow = { fyTotal: 0 };
        MONTH_ABBREVIATIONS.forEach((abbr) => {
            grandRow[`m_${abbr}`] = 0;
        });
        QUARTER_KEYS.forEach((quarter) => {
            grandRow[`q_${quarter}`] = 0;
        });

        groups.forEach((group) => {
            (group.projects || [])
                .filter((project) => {
                    if (unit === "ALL") return true;
                    // Hide projects with no data under the currently selected unit.
                    return (project.units || []).some((u) => u.MNF_UNIT === unit);
                })
                .forEach((project) => {
                    const fyVal = project.financial_years?.[selectedFY] ?? "0.00";

                    // Skip projects with no contribution in the selected FY.
                    if (toNumber(fyVal) === 0) return;

                    const row = {
                        key: makeProjectKey(project.IO_NO, project.REV_NO),
                        IO_NO: project.IO_NO,
                        projectName: project.PROJ_NAME,
                    };

                    row.fyTotal = fyVal;
                    grandRow.fyTotal += toNumber(fyVal);

                    MONTH_ABBREVIATIONS.forEach((abbr) => {
                        const mVal = project.months?.[getMonthKey(abbr, selectedFYStartYear)] ?? "0.00";
                        row[`m_${abbr}`] = mVal;
                        grandRow[`m_${abbr}`] += toNumber(mVal);
                    });

                    QUARTER_KEYS.forEach((quarter) => {
                        const qVal = project.quarters?.[getQuarterKey(quarter, selectedFYStartYear)] ?? "0.00";
                        row[`q_${quarter}`] = qVal;
                        grandRow[`q_${quarter}`] += toNumber(qVal);
                    });

                    rows.push(row);
                });
        });

        // A summed grand total of percentages isn't a meaningful figure, so
        // the bottom Total row is hidden in Percent mode (same rule as
        // DMReport's Overall row).
        if (valueMode === "PERCENT") {
            return rows;
        }

        const totalRow = {
            key: "grand-total",
            projectName: "Total",
            isTotalRow: true,
            fyTotal: grandRow.fyTotal.toFixed(2),
        };
        MONTH_ABBREVIATIONS.forEach((abbr) => {
            totalRow[`m_${abbr}`] = grandRow[`m_${abbr}`].toFixed(2);
        });
        QUARTER_KEYS.forEach((quarter) => {
            totalRow[`q_${quarter}`] = grandRow[`q_${quarter}`].toFixed(2);
        });

        return [...rows, totalRow];
    // }, [groups, unit, selectedFY, selectedFYStartYear, valueMode, isOverallView, visibleYearLabels]);
    }, [groups, unit, selectedFY, selectedFYStartYear, valueMode, isOverallView, individualYearLabels, beyondYearLabels, CURRENT_FY_START]);

    return (
        <div className="pb-14">
            <div className="px-5 flex flex-col gap-6 pt-8">
                {/* HEADER */}
                <div className="flex gap-3">
                    <img src={PMGProjectIcon} alt="icon" />

                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            {costLevel !== "DIRECT_MATERIAL" ? (
                                <span>{costLevel.split("_").join(" ")} of Critical/Major Products</span>
                            ) : (
                                <span>Direct Material Cost of Critical/Major Products</span>
                            )}
                        </div>

                        <div className="text-gray-500 text-sm">* All figures are in Rs Crores or in Percent depending on selected filter.</div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-6 mt-2">
                    <div className="flex flex-wrap items-start justify-start gap-4 p-4 bg-[#F9FAFB] border border-[#EAECF0] rounded-xl">
                        {/* Cost Level */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">Cost Level</span>
                            <CustomSelectWithFilter
                                data={COST_LEVEL_OPTIONS}
                                value={costLevel}
                                onChange={setCostLevel}
                                placeholder="Cost Level"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Value Mode */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">Value Mode</span>
                            <CustomSelectWithFilter
                                data={VALUE_MODE_OPTIONS}
                                value={valueMode}
                                onChange={setValueMode}
                                placeholder="Value Mode"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* FY */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">Financial Year</span>
                            <CustomSelectWithFilter
                                data={fyOptions}
                                value={selectedFY}
                                onChange={(value) => setSelectedFY(value || DEFAULT_FY_KEY)}
                                placeholder="FY"
                                className="w-[160px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Unit */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">Unit</span>
                            <CustomSelectWithFilter
                                data={unitOptions}
                                value={unit}
                                onChange={setUnit}
                                placeholder="Unit"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Projects Filter */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">Projects Filter</span>
                            <CustomMultiSelect
                                data={visibleProjectsForFilter.map((project) => ({
                                    label: project.PROJ_NAME,
                                    value: makeProjectKey(project.IO_NO, project.REV_NO),
                                }))}
                                value={selectedProjectKeys}
                                onChange={setSelectedProjectKeys}
                                placeholder="All Projects"
                                className="w-[280px]"
                            />
                        </div>

                        {/* Product — only when the selected Unit is of type UNIT. */}
                        {showProductFilter && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">Product</span>
                                <CustomMultiSelect
                                    data={productOptionsForUnit}
                                    value={selectedProducts}
                                    onChange={setSelectedProducts}
                                    placeholder="All Products"
                                    className="w-[220px]"
                                />
                            </div>
                        )}

                        {/* Package — only when the selected Unit is of type REGION. */}
                        {showPackageFilter && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">Package</span>
                                <CustomMultiSelect
                                    data={packageOptions}
                                    value={selectedPackages}
                                    onChange={setSelectedPackages}
                                    placeholder="All Packages"
                                    className="w-[220px]"
                                />
                            </div>
                        )}

                        {/* Show Upto March — only meaningful in the Overall view with Crore mode */}
                        {isOverallView && valueMode !== "PERCENT" && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">
                                    Inc. Upto March '{String(CURRENT_FY_START).slice(-2)}
                                </span>
                                <div className="flex items-center h-[42px] px-3 rounded-md border border-[#3b82f6] bg-[#eff6ff]">
                                    <Switch
                                        checked={showUptoMarch}
                                        onChange={setShowUptoMarch}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                {gettingProjectWiseCost ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-8 w-8 animate-spin text-[#155EEF]" />
                    </div>
                ) : (
                    <div className="border border-[#EAECF0] rounded-xl overflow-hidden">
                        <CustomTable
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            scroll={{ x: "max-content", y: 600 }}
                            rowClassName={(record) => (record.isTotalRow ? "dm-grand-total-row" : "")}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DMCriticalProductsReport;
